import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth';
import { isAdminUser } from '@/utils/admin';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
  requireParticipant?: boolean;
}

export function ProtectedRoute({ 
  children, 
  requireAuth = true, 
  requireAdmin = false,
  requireParticipant = false 
}: ProtectedRouteProps) {
  const { user, participantProfile, isLoadingParticipant } = useAuth();
  const location = useLocation();

  // If authentication is required but user is not logged in
  if (requireAuth && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If admin access is required but user is not admin
  if (requireAdmin && (!user || !isAdminUser(user))) {
    return <Navigate to="/" replace />;
  }

  // If participant profile is required
  if (requireParticipant && !isLoadingParticipant) {
    if (!participantProfile?.hasProfile) {
      return <Navigate to="/annotation/demographics" replace />;
    }
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: React.ReactNode;
}

export function PublicRoute({ children }: PublicRouteProps) {
  const { user } = useAuth();
  const location = useLocation();

  // If user is already authenticated, redirect to home or intended page
  if (user) {
    const from = (location.state as any)?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}